import CloseIcon from '@mui/icons-material/Close';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { Box, Button, Paper, Typography } from '@mui/material';
import i18n from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BannersContainer from '../../components/BannersContainer';
import MovingCard from '../../components/MovingCard';
import SortMenu from '../../components/SortMenu';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectIsSearch, selectParams, selectParamsFilter, selectProductListItems, selectProductListStatus, selectIsAddMoreLoading, selectProductListHasMore, selectCurrentSearch, selectSelectedCurrency, selectUserId } from '../../redux/selectors';
import { resetParams } from '../../redux/slices/params';
import { fetchItems, ProductListParams, resetSearch, setCurrentSearch } from '../../redux/slices/productList';
import Categories from './Categories';
import SelectLocation from '../../components/SelectLocation';
import HomeProductGrid from './HomeProductGrid';
import { Spinner } from './Home.styles';

export { ScrollableContainer, ProductGrid, ItemWrapper, Spinner } from './Home.styles';

interface IProps {}
const Home: React.FC<IProps> = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const userId = useAppSelector(selectUserId);
    const data = useAppSelector(selectProductListItems);
    const status = useAppSelector(selectProductListStatus);
    const isLoadingMore = useAppSelector(selectIsAddMoreLoading);
    const hasMore = useAppSelector(selectProductListHasMore);
    const CurrentSearch = useAppSelector(selectCurrentSearch);
    const isSearched = useAppSelector(selectIsSearch);
    const paramFilter = useAppSelector(selectParamsFilter);
    const paramState = useAppSelector(selectParams);
    const selectedCurrency = useAppSelector(selectSelectedCurrency);
    const showCurrency = selectedCurrency || 'UZS';

    const loading = status === 'loading';
    const [chosenSortValue, setChosenSortValue] = React.useState('');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const sortRef = React.useRef<{ columName: string; type: 'asc' | 'desc' } | null>(null);
    const filterRef = React.useRef<Record<string, unknown>>({});

    const handleSort = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const buildQuery = React.useCallback(
        (overrides: Record<string, unknown> = {}): Partial<ProductListParams> => ({
            ...paramState,
            sortby: (sortRef.current ?? paramState?.sortby) as ProductListParams['sortby'],
            filter: { ...(paramState?.filter ?? {}), ...(filterRef.current || {}) } as ProductListParams['filter'],
            userId,
            lang: i18n.language,
            showCurrency,
            ...overrides
        }),
        [paramState, showCurrency, userId]
    );
    const lastSortInvocationRef = React.useRef<{ key: string; at: number } | null>(null);

    const sortFunc = React.useCallback(
        (sortParams: { columName: string; type: 'asc' | 'desc' }, filterParams?: Record<string, unknown>) => {
            const key = `${sortParams.columName}-${sortParams.type}-${JSON.stringify(filterParams ?? {})}`;
            const now = Date.now();
            if (lastSortInvocationRef.current?.key === key && now - lastSortInvocationRef.current.at < 500) {
                return;
            }
            lastSortInvocationRef.current = { key, at: now };

            sortRef.current = sortParams;
            filterRef.current = filterParams ?? {};
            dispatch(setCurrentSearch(''));

            const mergedFilter = { ...(paramState?.filter ?? {}), ...(filterParams ?? {}) } as Partial<ProductListParams>['filter'];
            dispatch(
                fetchItems(
                    {
                        sortby: sortParams,
                        filter: mergedFilter,
                        pageNumber: 1,
                        userId,
                        lang: i18n.language,
                        showCurrency
                    },
                    false
                )
            );
        },
        [dispatch, paramState?.filter, userId, showCurrency]
    );

    const onLoadMore = React.useCallback(() => {
        dispatch(fetchItems(buildQuery(), true));
    }, [buildQuery, dispatch]);

    const handleClearSearch = () => {
        dispatch(resetParams());
        dispatch(resetSearch());
    };

    React.useEffect(() => {
        if (paramFilter?.parentCategoryId === 0 && paramFilter?.categoryId === 0) {
            filterRef.current = {};
        }
    }, [paramFilter?.parentCategoryId, paramFilter?.categoryId]);

    React.useEffect(() => {
        dispatch(fetchItems(buildQuery()));
    }, [userId, buildQuery, dispatch]);

    React.useEffect(() => {
        const handleLangChange = () => {
            dispatch(fetchItems(buildQuery()));
        };
        i18n.on('languageChanged', handleLangChange);
        return () => {
            i18n.off('languageChanged', handleLangChange);
        };
    }, [dispatch, buildQuery]);

    return (
        <>
            <Paper sx={{ p: 2, justifyContent: 'center', justifyItems: 'baseline', borderRadius: 6 }} elevation={0}>
                <BannersContainer />
            </Paper>

            <Categories />

            {CurrentSearch.length === 0 && <MovingCard />}

            <Paper elevation={0}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignContent: 'space-between', px: 2 }}>
                    <SelectLocation />
                    <Box>
                        {!!CurrentSearch && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1" component="span" sx={{ margin: 2, fontWeight: 400 }}>
                                    {t('web.main-searchedString')}{' '}
                                    <Typography component="span" color="warning.main" fontWeight={600} variant="body1">
                                        &apos;{CurrentSearch}&apos;
                                    </Typography>
                                </Typography>
                                {!isSearched && <CloseIcon onClick={handleClearSearch} />}
                            </Box>
                        )}
                        <Typography variant="subtitle1" align="left" sx={{ fontSize: 18, fontWeight: 600, m: 3 }}>
                            {chosenSortValue}
                        </Typography>
                    </Box>

                    <Button endIcon={<ImportExportIcon />} onClick={handleSort}>
                        <Typography>{t('sort_by.sort')}</Typography>
                    </Button>
                </Box>

                {!loading && !data?.length ? (
                    <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', py: 3 }}>
                        <Typography variant="body1" component="p" fontWeight={600}>
                            {t('web.main-noItems')}
                        </Typography>
                    </Box>
                ) : (
                    <HomeProductGrid
                        loading={loading}
                        items={data ?? []}
                        hasMore={hasMore}
                        isLoadingMore={isLoadingMore}
                        onLoadMore={onLoadMore}
                    />
                )}

            </Paper>

            <SortMenu open={open} onClose={handleClose} anchorEl={anchorEl} setChosenSortValue={setChosenSortValue} sortFunction={sortFunc} />
        </>
    );
};

export default Home;
